import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import AppLayout from './components/layout/AppLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import LoginPage from './components/auth/LoginPage'
import UsersPage from './pages/UsersPage'
import PolicyConfigPage from './pages/PolicyConfigPage'
import PolicyPage from './v2/PolicyPage'
import EqsAdminPage from './pages/EqsAdminPage'
import PromptOpsPage from './pages/PromptOpsPage'
import SystemHealthPage from './pages/SystemHealthPage'
import ChainReviewPage from './pages/ChainReviewPage'
import OrphansPage from './pages/OrphansPage'
import V2Shell from './v2/V2Shell'
import RkDecksPage from './v2/rkdecks/RkDecksSurface'
import { TasksRoute, WorkbenchDeepLink, WorkbenchGate } from './v2/workbench/routes'
import SurfaceStream from './v2/workbench/SurfaceStream'
import ReconciliationSurface from './v2/workbench/ReconciliationSurface'
import TaskPaneHost from './v2/workbench/TaskPaneHost'
import OtherOpsPage, { SystemFailuresAdminPage } from './v2/ops/OtherOpsSurface'
import FundCockpit from './v2/FundCockpit'
import FundsPage from './v2/FundsPage'
import ContextRecordPage from './v2/navigation/ContextRecordPage'
import FundHistory from './v2/navigation/FundHistory'
import SubchainOverview from './v2/navigation/SubchainOverview'
import DocumentsPage from './v2/DocumentsPage'
import DocumentDetailPage from './v2/DocumentDetailPage'
import DocumentReader from './v2/documents/DocumentReader'
import ManualEntry from './v2/ManualEntry'
import Home from './v2/Home'
import AdminHome, { AdminFrame } from './v2/AdminFrame'
import LoginsPage from './v2/admin/LoginsPage'
import ReviewCommentsPage from './v2/reviewComments/ReviewCommentsPage'

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<LoginPage />} />

          {/* A document is its own protected window, with no workbench shell. */}
          <Route
            path="/v2/documents/:id"
            element={
              <ProtectedRoute>
                <DocumentReader />
              </ProtectedRoute>
            }
          />
          <Route
            path="/v2/traces/:id"
            element={
              <ProtectedRoute>
                <DocumentReader />
              </ProtectedRoute>
            }
          />

          {/* Protected routes within app shell */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* The v1 officer surface is RETIRED (2026-07-29). It reported the
                same funds with different numbers than v2 — a green "100%
                verified" ring beside a v2 cockpit showing amber with open
                breaks — because the honest-verified work landed on v2 only.
                The Policy screen taught the lesson: a stale surface that looks
                authoritative is worse than no surface, and leaving it merely
                unlinked is how it survives. Routes removed so nothing can
                reach it; every path below now lands on the officer app. */}
            <Route path="/dashboard" element={<Navigate to="/v2" replace />} />
            <Route path="/funds" element={<Navigate to="/v2/funds" replace />} />
            <Route path="/funds/:id" element={<Navigate to="/v2/funds" replace />} />
            {/* Per-event review RETIRED 2026-08-02 (Ale decision 8): nothing
                in production linked here, and the surface lagged the
                entity-merge ruling. The v2 workbench (?task= deep link) and
                /review/:taskId are the review surfaces; proposal-review.spec
                now drives v2. */}
            <Route
              path="/funds/:id/events/:eventId"
              element={<Navigate to="/v2/tasks" replace />}
            />
            <Route path="/tasks" element={<Navigate to="/v2/tasks" replace />} />
            {/* The spec's deep-link form, forwarded into the v2 shell. */}
            <Route path="/workbench" element={<WorkbenchDeepLink />} />
            <Route path="/workbench/:surface" element={<WorkbenchDeepLink />} />
            <Route path="/requests" element={<Navigate to="/v2/tasks" replace />} />
            <Route path="/upload" element={<Navigate to="/v2/traces" replace />} />
            {/* /orphans is NOT a v1 reminiscence: it is the live parked-
                documents surface, with deep links into manual object entry,
                and the OFFICER suite drives it. Deleting it broke
                orphan-queue.spec.ts — caught by e2e-officer, which is exactly
                why that suite is the one to trust on a UI deletion. */}
            <Route path="/orphans" element={<OrphansPage />} />
            {/* Chain review is SHARED with v2 and stays reachable — the v2
                workbench links into it by task id. */}
            <Route path="/review" element={<ChainReviewPage />} />
            <Route path="/review/:taskId" element={<ChainReviewPage />} />

            {/* Admin-only routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={['admin']}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/eqs"
              element={
                <ProtectedRoute roles={['admin']}>
                  <EqsAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/policy"
              element={
                <ProtectedRoute roles={['admin', 'manager', 'ops_officer']}>
                  <PolicyConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/health"
              element={
                <ProtectedRoute roles={['admin']}>
                  <SystemHealthPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* v2 redesign — skeleton stage, own shell (no AppLayout) */}
          <Route
            path="/v2"
            element={
              <ProtectedRoute>
                <V2Shell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Home />} />
            <Route
              path="comments"
              element={
                <ProtectedRoute roles={['admin', 'manager']}>
                  <ReviewCommentsPage />
                </ProtectedRoute>
              }
            />
            {/* Flag off: the current TasksWorkbench, byte-identical. Flag
                on: redirect BY TASK TYPE into the owning surface (W6). */}
            <Route path="tasks" element={<TasksRoute />} />
            {/* The workbench (W6) — four surface lines, then per-surface
                streams. Dark behind rk_decks like the deck surface. */}
            <Route path="workbench" element={<WorkbenchGate />} />
            {/* Reconciliation view (Wave B2): its own surface, before the
                :surface param so the slug wins. */}
            <Route
              path="workbench/reconciliation"
              element={
                <main className="wb">
                  <div className="inner">
                    <ReconciliationSurface />
                  </div>
                </main>
              }
            />
            <Route path="workbench/:surface" element={<SurfaceStream />} />
            <Route path="workbench/:surface/task/:taskId" element={<TaskPaneHost />} />
            {/* RK deck surface (W4) — dark behind the rk_decks flag; the
                page itself probes /api/rk/decks and renders a stub while
                the namespace 404s. */}
            <Route path="proposals" element={<RkDecksPage />} />
            {/* Other Ops — the officer's system-failures line (the digest
                card left 2026-09-04, audit cut 1),
                dark behind the same rk_decks flag (the page probes
                /api/ops/cards). W6's /workbench/ops stream supersedes this
                standalone route at cutover; it renders <OtherOpsSurface/>
                directly. The admin view carries the diagnosis cards —
                machine failures are admin work (Ale 2026-08-09); the
                endpoint 403s a non-admin either way. */}
            <Route path="ops" element={<OtherOpsPage />} />
            <Route
              path="ops/system-failures"
              element={
                <ProtectedRoute roles={['admin']}>
                  <SystemFailuresAdminPage />
                </ProtectedRoute>
              }
            />
            <Route path="funds" element={<FundsPage />} />
            <Route path="record" element={<ContextRecordPage />} />
            <Route path="subchain/:id" element={<SubchainOverview />} />
            <Route path="funds/:id" element={<FundCockpit />} />
            <Route path="funds/:id/history" element={<FundHistory />} />
            {/* Documents (Traces until 2026-09-03; URL /v2/traces kept; Evidence = fund tab): documents in + the audit
                trail out. The old /v2/documents paths stay as ALIASES so
                every existing deep link keeps working. */}
            <Route path="traces" element={<DocumentsPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="documents/:id/details" element={<DocumentDetailPage />} />
            {/* Standalone manual entry needs the surface chrome itself —
                without the main.detail wrapper the fund chooser floated on
                an unpainted void at day 0. Embedded uses (TakeOverPane)
                bring their own container. */}
            <Route
              path="manual"
              element={
                <main className="detail">
                  <ManualEntry />
                </main>
              }
            />

            {/* Admin section — hosted in the v2 shell, role-gated. Replaces the
                old v1 AppLayout admin pages (which stay reachable by URL until
                v1 is fully retired). */}
            <Route
              path="admin"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminHome />
                </ProtectedRoute>
              }
            />
            {/* The v2 Policy nav used to mount the v1 PolicyConfigPage, which
                edits policy-v1 structures no live code reads (POLICY_V2_CUTOVER
                defaults true). It now shows the catalogue the engine actually
                runs on. */}
            <Route
              path="admin/policy"
              element={
                <ProtectedRoute roles={['admin', 'manager']}>
                  <AdminFrame title="Policy">
                    <PolicyPage />
                  </AdminFrame>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminFrame title="Users">
                    <UsersPage />
                  </AdminFrame>
                </ProtectedRoute>
              }
            />
            {/* LOGIN TRACKING (Ale 2026-09-02): who signed in, from where,
                and the live sessions with their end door. Admin-only. */}
            <Route
              path="admin/logins"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminFrame title="Logins">
                    <LoginsPage />
                  </AdminFrame>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/health"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminFrame title="System health">
                    <SystemHealthPage />
                  </AdminFrame>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/eqs"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminFrame title="Extraction quality">
                    <EqsAdminPage />
                  </AdminFrame>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/prompt-ops"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminFrame title="Prompt optimization">
                    <PromptOpsPage />
                  </AdminFrame>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch-all redirect */}
          {/* Front-door cutover (2026-06-22): land in the v2 cockpit. The v1
              routes above stay reachable (admin/policy, deep links, /review). */}
          <Route path="*" element={<Navigate to="/v2" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
